// @ts-ignore
/* eslint-disable */
import { BusinessInfo } from "../types/businessTypes";
import {
  PROHIBITED_GEOS,
  PROHIBITED_INDUSTRIES,
  CountryCodeAlpha2,
  USStateCodeAlpha2,
} from "./constants";

// Platform customer use cases
const PLATFORM_CUSTOMERS = [
  "Individuals that will transfer money to each other",
  "Businesses that will pay their employees, clients, or end users",
];

// Group definitions with rules and template IDs
const GROUPS = {
  group1: {
    name: "B2B",
    number: 1,
    personaTemplateId: "itmpl_nfaaKTTcsZTc7CFgjiRcZdfT",
  },
  group2: {
    name: "Platform",
    number: 2,
    // personaTemplateId: "itmpl_bEBV4c3CSB4tsPvBsBhA28zXSdSL",
    // TEMPORARY: use the old template for now
    personaTemplateId: "itmpl_nfaaKTTcsZTc7CFgjiRcZdfT",
  },
  group3: {
    name: "Hand-off",
    number: 3,
    // personaTemplateId: "itmpl_bEBV4c3CSB4tsPvBsBhA28zXSdSL",
    // TEMPORARY: use the old template for now
    personaTemplateId: "itmpl_nfaaKTTcsZTc7CFgjiRcZdfT",
  },
  group4: {
    name: "Rejected",
    number: 4,
    personaTemplateId: null,
  },
};

/**
 * Helper function to check if a business is in a prohibited location
 */
function isProhibitedLocation(businessInfo: BusinessInfo): boolean {
  // Check if country is prohibited
  if (
    PROHIBITED_GEOS.countries.includes(
      businessInfo.hqCountry as CountryCodeAlpha2,
    )
  ) {
    return true;
  }

  // Check if state is prohibited (only for US businesses)
  if (
    businessInfo.hqCountry === CountryCodeAlpha2.US &&
    PROHIBITED_GEOS.states.includes(businessInfo.hqState as USStateCodeAlpha2)
  ) {
    return true;
  }

  return false;
}

/**
 * Helper function to convert monthly volume string to numeric value for comparison
 */
function getMonthlyVolumeValue(volumeString: string): number {
  if (volumeString === "None") return 0;
  if (volumeString === "<$100K") return 99999;
  if (volumeString === "$100K-$500K") return 499999;
  if (volumeString === "$500k-$1M") return 999999;
  if (volumeString === "$1M-$10M") return 9999999;
  if (volumeString === "$10M-$50M") return 49999999;
  if (volumeString === ">$50M") return 50000000;
  return 0;
}

/**
 * Helper function to convert transactions per month string to numeric value
 */
function getTransactionsPerMonthValue(transactionsString: string): number {
  if (transactionsString === "None") return 0;
  if (transactionsString === "<100") return 99;
  if (transactionsString === "100-1K") return 999;
  if (transactionsString === "1K-10K") return 9999;
  if (transactionsString === "10k+") return 10000;
  return 0;
}

/**
 * Helper function to check if any use cases match the platform customers list
 */
function isPlatformCustomer(customerTypes: string[]): boolean {
  return customerTypes.some((customerType) =>
    PLATFORM_CUSTOMERS.includes(customerType),
  );
}

/**
 * Helper function to check if useCase includes U.S. market entry
 */
function isUSMarketEntry(useCases: string[]): boolean {
  return useCases.includes("U.S. market entry");
}

/**
 * Group 1 (B2B) - Not in prohibited industries/locations AND not platform customers
 */
function isGroup1(businessInfo: BusinessInfo): boolean {
  // Not in prohibited industry or location
  const passesProhibitedCheck =
    !PROHIBITED_INDUSTRIES.includes(businessInfo.industry) &&
    !isProhibitedLocation(businessInfo);

  // Not platform customer
  const notPlatformCustomer = !isPlatformCustomer(businessInfo.customerTypes);

  return passesProhibitedCheck && notPlatformCustomer;
}

/**
 * Group 2 (Platform) - Not in prohibited industries/locations AND is platform customer
 */
function isGroup2(businessInfo: BusinessInfo): boolean {
  // Not in prohibited industry or location
  const passesProhibitedCheck =
    !PROHIBITED_INDUSTRIES.includes(businessInfo.industry) &&
    !isProhibitedLocation(businessInfo);

  // Is platform customer
  const isPlatform = isPlatformCustomer(businessInfo.customerTypes);

  return passesProhibitedCheck && isPlatform;
}

/**
 * Group 3 (Hand-off) - Not in prohibited industries/locations AND high volume OR high transactions AND U.S. market entry
 */
function isGroup3(businessInfo: BusinessInfo): boolean {
  // Not in prohibited industry or location
  const passesProhibitedCheck =
    !PROHIBITED_INDUSTRIES.includes(businessInfo.industry) &&
    !isProhibitedLocation(businessInfo);

  // High volume/transaction requirements
  const monthlyVolume = getMonthlyVolumeValue(businessInfo.monthlyVolume);
  const transactionsPerMonth = getTransactionsPerMonthValue(
    businessInfo.transactionsPerMonth,
  );

  const highVolumeOrTransactions =
    monthlyVolume > 10000000 || transactionsPerMonth >= 10000;

  // U.S. market entry use case
  const usMarketEntry = isUSMarketEntry(businessInfo.useCase);

  return passesProhibitedCheck && highVolumeOrTransactions && usMarketEntry;
}

/**
 * Group 4 (Rejected) - In prohibited industries OR locations
 */
function isGroup4(businessInfo: BusinessInfo): boolean {
  return (
    PROHIBITED_INDUSTRIES.includes(businessInfo.industry) ||
    isProhibitedLocation(businessInfo)
  );
}

/**
 * Determine the appropriate persona template ID based on business information
 */
export function getPersonaTemplateId(
  businessInfo: BusinessInfo,
): string | null {
  if (isGroup4(businessInfo)) {
    return GROUPS.group4.personaTemplateId;
  } else if (isGroup3(businessInfo)) {
    return GROUPS.group3.personaTemplateId;
  } else if (isGroup2(businessInfo)) {
    return GROUPS.group2.personaTemplateId;
  } else if (isGroup1(businessInfo)) {
    return GROUPS.group1.personaTemplateId;
  }

  // Default to B2B template if no rules match (fallback)
  return GROUPS.group1.personaTemplateId;
}

/**
 * Get the group number (1-4) for the business
 */
export function getBusinessGroup(businessInfo: BusinessInfo): number {
  if (isGroup4(businessInfo)) {
    return GROUPS.group4.number;
  } else if (isGroup3(businessInfo)) {
    return GROUPS.group3.number;
  } else if (isGroup2(businessInfo)) {
    return GROUPS.group2.number;
  } else if (isGroup1(businessInfo)) {
    return GROUPS.group1.number;
  }

  // Default to group 1 if no rules match
  return GROUPS.group1.number;
}

/**
 * Get the group name for the business
 */
export function getBusinessGroupName(businessInfo: BusinessInfo): string {
  if (isGroup4(businessInfo)) {
    return GROUPS.group4.name;
  } else if (isGroup3(businessInfo)) {
    return GROUPS.group3.name;
  } else if (isGroup2(businessInfo)) {
    return GROUPS.group2.name;
  } else if (isGroup1(businessInfo)) {
    return GROUPS.group1.name;
  }

  // Default to group 1 if no rules match
  return GROUPS.group1.name;
}
